/**
 * DateSelection Component
 *
 * Calendar-based date picker for booking dates with availability display.
 * Uses the shared DateRangeCalendar component.
 */

import React, { useState, useMemo } from 'react';
import { DateRangeCalendar } from '../../../components/calendar';

interface DateSelectionProps {
  checkIn: Date | null;
  checkOut: Date | null;
  onChange: (checkIn: Date, checkOut: Date) => void;
  blockedDates?: Set<string>;
  minimumStay?: number;
  maximumStay?: number;
}

const formatDisplayDate = (date: Date): string => {
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
};

const differenceInDays = (end: Date, start: Date): number => {
  return Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
};

export const DateSelection: React.FC<DateSelectionProps> = ({
  checkIn,
  checkOut,
  onChange,
  blockedDates = new Set<string>(),
  minimumStay = 1,
  maximumStay,
}) => {
  const [isEditing, setIsEditing] = useState(false);

  const nights = useMemo(() => {
    if (!checkIn || !checkOut) return 0;
    return differenceInDays(checkOut, checkIn);
  }, [checkIn, checkOut]);

  const handleCalendarChange = (newCheckIn: Date | null, newCheckOut: Date | null) => {
    if (newCheckIn && newCheckOut) {
      onChange(newCheckIn, newCheckOut);
      setIsEditing(false);
    }
  };

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden mb-4">
      {/* Collapsed View */}
      {!isEditing && checkIn && checkOut && (
        <button
          onClick={() => setIsEditing(true)}
          className="w-full p-4 text-left hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-500 mb-1">Dates</div>
              <div className="font-medium">
                {formatDisplayDate(checkIn)} - {formatDisplayDate(checkOut)}
              </div>
              <div className="text-sm text-gray-500 mt-1">
                {nights} night{nights !== 1 ? 's' : ''}
              </div>
            </div>
            <span className="text-primary-600 text-sm font-medium">Edit</span>
          </div>
        </button>
      )}

      {/* Expanded View with Calendar */}
      {(isEditing || !checkIn || !checkOut) && (
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <span className="font-medium">Select dates</span>
            {checkIn && checkOut && (
              <button
                onClick={() => setIsEditing(false)}
                className="text-primary-600 text-sm font-medium"
              >
                Done
              </button>
            )}
          </div>

          <DateRangeCalendar
            checkIn={checkIn}
            checkOut={checkOut}
            onChange={handleCalendarChange}
            blockedDates={blockedDates}
            minimumStay={minimumStay}
            maximumStay={maximumStay}
          />

          {/* Stay info */}
          {minimumStay > 1 && (
            <p className="text-sm text-gray-500 mt-3">
              Minimum stay: {minimumStay} nights
            </p>
          )}

          {maximumStay && (
            <p className="text-sm text-gray-500 mt-1">
              Maximum stay: {maximumStay} nights
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default DateSelection;
